/**
 * Toast Service
 * Manages toast notifications
 */

export type ToastType = "success" | "error" | "info" | "warning";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

type ToastCallback = (toasts: Toast[]) => void;

class ToastService {
  private toasts: Toast[] = [];
  private callbacks: Set<ToastCallback> = new Set();
  private timeoutMap: Map<string, ReturnType<typeof setTimeout>> = new Map();

  /**
   * Subscribe to toast changes
   */
  subscribe(callback: ToastCallback): () => void {
    this.callbacks.add(callback);
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Show a toast
   */
  show(message: string, type: ToastType = "info", duration: number = 5000): void {
    const existingToast = this.toasts.find(
      (toast) => toast.message === message && toast.type === type
    );
    if (existingToast) {
      // Refresh duration instead of creating another toast with same message/type.
      this.toasts = this.toasts.map((toast) =>
        toast.id === existingToast.id ? { ...toast, duration } : toast
      );
      this.notify();
      this.scheduleAutoRemove(existingToast.id, duration);
      return;
    }

    const id = Math.random().toString(36).substring(2, 9);
    const toast: Toast = { id, message, type, duration };

    this.toasts = [...this.toasts, toast];
    this.notify();
    this.scheduleAutoRemove(id, duration);
  }

  /**
   * Remove a toast
   */
  remove(id: string): void {
    const timeout = this.timeoutMap.get(id);
    if (timeout) {
      clearTimeout(timeout);
      this.timeoutMap.delete(id);
    }
    this.toasts = this.toasts.filter((toast) => toast.id !== id);
    this.notify();
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.timeoutMap.forEach((timeout) => clearTimeout(timeout));
    this.timeoutMap.clear();
    this.toasts = [];
    this.notify();
  }

  /**
   * Get current toasts
   */
  getToasts(): Toast[] {
    return this.toasts;
  }

  /**
   * Notify subscribers
   */
  private notify(): void {
    this.callbacks.forEach((callback) => callback([...this.toasts]));
  }

  private scheduleAutoRemove(id: string, duration: number): void {
    const existingTimeout = this.timeoutMap.get(id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.timeoutMap.delete(id);
    }

    if (duration > 0) {
      const timeout = setTimeout(() => {
        this.remove(id);
      }, duration);
      this.timeoutMap.set(id, timeout);
    }
  }
}

export const toastService = new ToastService();

/** Survives full page reload — flushed once on app mount (see ToastContainer). */
export const PENDING_TOAST_STORAGE_KEY = "andy_pending_toast_v1";

export function persistToastForNextLoad(
  message: string,
  type: ToastType = "info",
  duration: number = 5000
): void {
  try {
    sessionStorage.setItem(PENDING_TOAST_STORAGE_KEY, JSON.stringify({ message, type, duration }));
  } catch {
    // ignore quota / private mode
  }
}

export function flushPendingToastFromStorage(): void {
  try {
    const raw = sessionStorage.getItem(PENDING_TOAST_STORAGE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PENDING_TOAST_STORAGE_KEY);
    const data = JSON.parse(raw) as { message?: string; type?: ToastType; duration?: number };
    if (data?.message && data?.type) {
      toastService.show(data.message, data.type, data.duration ?? 5000);
    }
  } catch {
    sessionStorage.removeItem(PENDING_TOAST_STORAGE_KEY);
  }
}
